import dayjs from '../../utils/dayjs';
import { FormLayout, Modal, TextField, Form, Popover, DatePicker, Icon, Text, Card } from '@shopify/polaris';
import { CalendarIcon } from '@shopify/polaris-icons';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useField, useForm } from '@shopify/react-form';
import __ from 'languages/index';
import { useMutateCreateTripSchedule, useMutateGetTripSchedule, useUpdateTripSchedule } from 'queries/trip_schedule.query';
import helpers from 'helpers/index';


// --- Helper Functions ---
// Chuyển đổi object Date sang chuỗi 'YYYY-MM-DD'
function dateToYyyyMmDd(date) {
  const offset = date.getTimezoneOffset();
  const adjustedDate = new Date(date.getTime() - offset * 60 * 1000);
  return adjustedDate.toISOString().split('T')[0];
}

// --- Reusable Date Picker Component ---
// Component này kết hợp TextField, Popover và DatePicker
function DatePickerInput({ label, field, connectedLeft }) {
  const [visible, setVisible] = useState(false);
  const [selectedDate, setSelectedDate] = useState(field.value ? new Date(field.value) : new Date());

  const [{ month, year }, setDate] = useState({
    month: selectedDate.getMonth(),
    year: selectedDate.getFullYear(),
  });

  const handleDateChange = useCallback(
    (date) => {
      setSelectedDate(date);
      field.onChange(dateToYyyyMmDd(date));
      setVisible(false);
    },
    [field]
  );

  const handleMonthChange = useCallback((month, year) => {
    setDate({ month, year });
  }, []);

  const activator = (
    <TextField
      {...field}
      label={label}
      onFocus={() => setVisible(true)}
      prefix={<Icon source={CalendarIcon} />}
      autoComplete="off"
      clearButton
      onClearButtonClick={() => field.onChange('')}
      connectedLeft={connectedLeft}
    />
  );

  return (
    <Popover preferredPosition="below" active={visible} activator={activator} onClose={() => setVisible(false)}>
      <Card>
        <DatePicker
          month={month}
          year={year}
          onChange={({ start }) => handleDateChange(start)}
          onMonthChange={handleMonthChange}
          selected={field.value ? new Date(field.value) : undefined}
        />
      </Card>
    </Popover>
  );
}

// --- Main Modal Component ---
export default function TripScheduleModal({ onClose, show, currentTripSchedule, currentJourneyID }) {
  const { mutateAsync: createEntity, isPending: creating, isSuccess: creatingSuccess } = useMutateCreateTripSchedule();
  const { mutateAsync: getTripSchedule, data: entity, reset } = useMutateGetTripSchedule();
  const { mutateAsync: updateEntity, isPending: updating, isSuccess: updateSuccess } = useUpdateTripSchedule();

  useEffect(() => {
    if (currentTripSchedule) {
      getTripSchedule(currentTripSchedule);
    } else {
      reset();
    }
  }, [currentTripSchedule]);

  /** Tự động format giờ */
  const prevDigitsRef = useRef('');
  const handleTimeChange = (raw) => {
    const digits = raw.replace(/\D/g, '').slice(0, 4);
    const prevDigits = prevDigitsRef.current;
    const isAdding = digits.length > prevDigits.length;

    let formatted = raw;

    if (isAdding) {
      // chỉ format khi đang gõ thêm
      formatted = digits.length > 2 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : digits;
    } else {
      // nếu đang xóa: giữ nguyên dấu ':' nếu nó đang có trong raw
      if (raw.includes(':')) {
        formatted = raw;
      } else {
        // fallback: format lại theo digits để tránh lộn xộn
        formatted = digits.length > 2 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : digits;
      }
    }

    prevDigitsRef.current = digits;
    return formatted;
  };

  const { fields, submit, dirty } = useForm({
    fields: {
      // Các trường hiển thị trên UI
      fromDate: useField(entity?.valid_from ? dayjs(new Date(entity.valid_from)).format('YYYY-MM-DD') : ''),
      fromTime: useField(entity?.valid_from ? dayjs(new Date(entity.valid_from)).format('HH:mm') : '00:00'),
      toDate: useField(entity?.valid_to ? dayjs(new Date(entity.valid_to)).format('YYYY-MM-DD') : ''),
      toTime: useField(entity?.valid_to ? dayjs(new Date(entity.valid_to)).format('HH:mm') : ''),
    },
    async onSubmit(values) {
      try {
        if (currentTripSchedule) {
          await updateEntity({
            id: currentTripSchedule,
            valid_from: `${values.fromDate}T${values.fromTime || '00:00'}:00`,
            valid_to: values.toDate ? `${values.toDate}T${values.toTime || '23:59'}:00` : null,
          });
        } else {
          await createEntity({
            valid_from: `${values.fromDate}T${values.fromTime || '00:00'}:00`,
            valid_to: values.toDate ? `${values.toDate}T${values.toTime || '23:59'}:00` : null,
            journey_id: String(currentJourneyID),
          });
        }
      } catch (e) {
        console.error('Submission error:', e);
        // Xử lý lỗi từ server (nếu có)
      }
      return { status: 'success' };
    },
  });

  useEffect(() => {
    if (creatingSuccess || updateSuccess) {
      helpers.resetForm(fields);
      onClose();
      reset();
    }
  }, [creatingSuccess, updateSuccess]);

  const Actual_page = (
    <Modal
      open={show}
      onClose={() => onClose()}
      title={currentTripSchedule ? 'Cập nhật lại lịch' : 'Thêm lịch chạy'}
      primaryAction={{
        content: currentTripSchedule ? 'Cập nhật' : 'Tạo mới',
        disabled: !dirty,
        loading: creating || updating,
        onAction: submit,
      }}
      secondaryActions={[
        {
          content: 'Đóng',
          onAction: () => onClose(),
        },
      ]}
    >
      <Modal.Section>
        <Form onSubmit={submit}>
          <FormLayout>
            <Text variant="headingMd" as="h3">
              Thời gian hiệu lực
            </Text>
            <FormLayout.Group condensed>
              <DatePickerInput label="Từ ngày" field={fields.fromDate} connectedLeft={undefined} />
              <TextField
                label="Giờ bắt đầu"
                autoComplete="off"
                {...fields.fromTime}
                onChange={(v) => fields.fromTime.onChange(handleTimeChange(v))}
                clearButton
                onClearButtonClick={() => fields.fromTime.onChange('')}
              />
            </FormLayout.Group>

            <Text variant="headingMd" as="h3">
              Thời gian kết thúc
            </Text>
            <FormLayout.Group condensed>
              <DatePickerInput label="Đến ngày" field={fields.toDate} connectedLeft={undefined} />
              <TextField
                label="Giờ kết thúc"
                autoComplete="off"
                {...fields.toTime}
                onChange={(v) => fields.toTime.onChange(handleTimeChange(v))}
                clearButton
                onClearButtonClick={() => fields.toTime.onChange('')}
              />
            </FormLayout.Group>
          </FormLayout>
        </Form>
      </Modal.Section>
    </Modal>
  );

  return <>{Actual_page}</>;
}

